import { AxiosResponse } from 'axios';
import { debounce, isBoolean } from 'lodash-es';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { User } from '../interface/user.interface';
import {
  makeSOPClient,
  makeSOPClientProtocol,
  SOPClientApi,
  TOKEN_DEADLINE
} from '@barracuda/sop-api-client';
import { nowMilliseconds } from '../utils/time';

const debug = process.env.REACT_APP_MODE === 'dev';

const TOKEN_KEY = 'token';

const VALIDATE_TOKEN_SECONDS = 10;
const ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS = 30;
const ACTIVITY_TIMEOUT_MINUTES = 10;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

const REFRESH_TOKEN_MAX_RETRIES = 5;
const REFRESH_TOKEN_RETRY_DELAY_MS = 5_000;

export enum TokenRefreshErrorLevel {
  WARNING = 'warning',
  FATAL = 'fatal'
}

export interface TokenRefreshError {
  level: TokenRefreshErrorLevel;
  attempt: number;
  maxRetries: number;
  error: unknown;
}

type TokenRefreshErrorCallback = (event: TokenRefreshError) => void;

const tokenRefreshErrorListeners = new Set<TokenRefreshErrorCallback>();

let currentToken: string | undefined;
let refreshInProgress = false;
let lastActiveAt = nowMilliseconds();

let storageArea = window.localStorage;

export const setStorageArea = (area: Storage) => {
  storageArea = area;
};

export const markUserPresence = async () => {
  lastActiveAt = nowMilliseconds();
};

const markUserPresenceDebounced = debounce(
  markUserPresence,
  ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000,
  { leading: true, maxWait: ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000 }
);

ACTIVITY_EVENTS.forEach((eventName) => {
  document.addEventListener(eventName, markUserPresenceDebounced, {
    passive: true
  });
});

export const sopClient: SOPClientApi = makeSOPClient(
  makeSOPClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_URL as string
  })
);

const dispatchStorageEvent = (key: string, oldValue?: string, newValue?: string) => {
  if (debug) {
    console.log('Auth.dispatchStorageEvent', key, oldValue, newValue, storageArea);
  }

  window.dispatchEvent(
    new StorageEvent('storage', {
      storageArea,
      key,
      oldValue,
      newValue
    })
  );
};

// at first run it is not yet stored in sop client
export const getSopToken = () =>
  sopClient.session.getToken() ||
  sessionStorage.getItem(TOKEN_KEY) ||
  localStorage.getItem(TOKEN_KEY) ||
  undefined;

export const removeSopToken = () => {
  setSopToken();
};

export const loadExistingSopToken = () => {
  setSopToken(getSopToken());
};

export const setSopToken = (token?: string) => {
  if (isTokenExpired(token)) {
    token = undefined;
  }

  if (debug) {
    console.log(`Auth.setSopToken(${token})`);
  }

  const oldToken = currentToken;
  if (token) {
    currentToken = token;
    sopClient.session.setToken(token);
    storageArea.setItem(TOKEN_KEY, token);

    checkSopToken();
  } else {
    clearTimeout(sopTokenCheckTimeout);

    currentToken = undefined;
    sopClient.session.deleteToken();
    storageArea.removeItem(TOKEN_KEY);
  }

  if (oldToken !== currentToken) {
    dispatchStorageEvent(TOKEN_KEY, oldToken, currentToken);
  }
};

export interface GetOAuthUrlParams {
  verificationLicenseId?: number | null;
  ageConsentConfirmed?: boolean;
  termsAndPrivacyAccepted?: boolean;
  receiveMarketing?: boolean;
  desktop?: boolean;
}

export interface SignUpParams {
  email: string;
  password: string;
  captchaResult?: string;
  dateOfBirth?: string;
  countryOfResidenceId?: number;
  consents?: SignUpConsentsParams;
}

export interface AuthenticatedSignUpParams {
  stateToken: string;
  captchaResult?: string;
  dateOfBirth: string;
  countryOfResidenceId: number;
  consents: SignUpConsentsParams;
}

export type SignUpConsentsParams = {
  termsAndPrivacy: boolean;
  marketingAndOffers?: boolean;
  personalInformationCollectionAndUseForMarketing?: boolean;
  provisionOfPersonalInformationTo3rdParty?: boolean;
  overseasTransferOfPersonalInformation?: boolean;
  personalInformationCollection?: boolean;
  uniqueIdentificationInformationCollection?: boolean;
  sensitiveInformationCollection?: boolean;
};

export interface UserTokenInfo {
  token: string;
  user: User;
}

export const getOAuthUrl: (params: GetOAuthUrlParams) => string = ({
  verificationLicenseId,
  ageConsentConfirmed,
  termsAndPrivacyAccepted,
  receiveMarketing,
  desktop
}: GetOAuthUrlParams) => {
  const baseUrl = process.env.REACT_APP_PESC_URL;
  const redirectUrl = `${baseUrl}/api/authentication/oauth2/redirect/google`;
  const url = new URL(redirectUrl);
  const searchParams = new URLSearchParams();

  searchParams.append('url', `${baseUrl}/${desktop ? 'desktop-callback' : 'callback'}`);

  const searchParamsFlags: Record<string, boolean | undefined> = {
    ageConsentConfirmed,
    termsAndPrivacyAccepted,
    receiveMarketing
  };
  Object.entries(searchParamsFlags).forEach(([key, value]) => {
    if (isBoolean(value)) {
      searchParams.append(key, value.toString());
    }
  });
  searchParams.append('applicationCode', 'ODYSSEY');
  if (verificationLicenseId) {
    searchParams.append('verificationLicenseId', verificationLicenseId.toString());
  }
  url.search = searchParams.toString();

  return url.toString();
};

export const signUp = async ({
  email,
  password,
  captchaResult,
  dateOfBirth,
  countryOfResidenceId,
  consents
}: SignUpParams) => {
  const response = await sopClient.signUp({
    email,
    password,
    captchaResult,
    consents,
    dateOfBirth,
    countryOfResidenceId
  });

  if (response.status === 409) {
    return Promise.reject(response);
  }

  return response as AxiosResponse;
};

export const completeSSOSignUp = async ({
  stateToken,
  captchaResult,
  dateOfBirth,
  countryOfResidenceId,
  consents
}: AuthenticatedSignUpParams) => {
  const response = await sopClient.completeSSOSignUp({
    stateToken,
    captchaResult,
    dateOfBirth,
    countryOfResidenceId,
    consents
  });

  if (response.data?.token) {
    setSopToken(response.data.token);
  }

  return response as AxiosResponse<UserTokenInfo>;
};

export const signIn = async (
  email: string,
  password: string,
  ignoreSavingToken?: boolean,
  captchaResult?: string
) => {
  markUserPresence();

  const response = await sopClient.signIn(email, password, captchaResult);
  if (response.data.token && !ignoreSavingToken) {
    setSopToken(response.data.token);
  }

  return response;
};

export const logout = async (loggedBefore?: string) => {
  await addLoggedBefore(loggedBefore);
  await sopClient.logout();
  removeSopToken();
  storageArea.removeItem('recentOrder');
};

export const addLoggedBefore = async (loggedBefore?: string) => {
  const payload = {
    loggedBefore: 'true'
  };

  if (!loggedBefore) {
    await sopClient.addMetadataToProfile(payload);
  }
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const notifyTokenRefreshError = (event: TokenRefreshError) => {
  tokenRefreshErrorListeners.forEach((cb) => cb(event));
};

const refreshToken = async () => {
  for (let attempt = 1; attempt <= REFRESH_TOKEN_MAX_RETRIES; attempt++) {
    try {
      const response = await sopClient.refreshToken();
      const token = response?.data?.token;

      setSopToken(token);

      return;
    } catch (error) {
      console.log(`Token refresh attempt ${attempt}/${REFRESH_TOKEN_MAX_RETRIES} failed`, error);

      if (attempt < REFRESH_TOKEN_MAX_RETRIES) {
        notifyTokenRefreshError({
          level: TokenRefreshErrorLevel.WARNING,
          attempt,
          maxRetries: REFRESH_TOKEN_MAX_RETRIES,
          error
        });
        await delay(REFRESH_TOKEN_RETRY_DELAY_MS);
      } else {
        notifyTokenRefreshError({
          level: TokenRefreshErrorLevel.FATAL,
          attempt,
          maxRetries: REFRESH_TOKEN_MAX_RETRIES,
          error
        });
        removeSopToken();
      }
    }
  }
};

export const isTokenCloseToExpired = (token: string | undefined): boolean => {
  const timeLeft = getTokenExpirationTime(token);

  return timeLeft < TOKEN_DEADLINE;
};

export const isTokenExpired = (token: string | null | undefined) => {
  if (token) {
    try {
      return getTokenExpirationTime(token) <= 0;
    } catch (e) {
      console.log('Token is invalid', e);
    }
  }

  return true;
};

export const getTokenExpirationTime = (token: string | undefined): number => {
  if (token) {
    try {
      const decodedToken: { exp: number } = jwt_decode(token);

      return moment(moment(decodedToken?.exp * 1000)).diff(nowMilliseconds(), 'seconds');
    } catch (e) {
      console.log('Token is invalid', e);
    }
  }

  return 0;
};

let sopTokenCheckTimeout: NodeJS.Timer | undefined;

const checkSopToken = () => {
  clearTimeout(sopTokenCheckTimeout);
  sopTokenCheckTimeout = setTimeout(() => checkSopToken(), VALIDATE_TOKEN_SECONDS * 1000);

  const sopToken = getSopToken();
  if (sopToken) {
    if (isTokenExpired(sopToken)) {
      removeSopToken();

      return;
    }

    if (!refreshInProgress && isTokenCloseToExpired(sopToken)) {
      const now = nowMilliseconds();
      const howManyMinutesAgoWasActive = (now - lastActiveAt) / (1000 * 60);
      if (howManyMinutesAgoWasActive < ACTIVITY_TIMEOUT_MINUTES) {
        refreshInProgress = true;

        refreshToken().finally(() => (refreshInProgress = false));
      }
    }
  }
};

export const addTokenRefreshErrorListener = (callback: TokenRefreshErrorCallback): (() => void) => {
  tokenRefreshErrorListeners.add(callback);

  return () => {
    tokenRefreshErrorListeners.delete(callback);
  };
};

export const addTokenUpdatedListener = (callback: () => void): (() => void) => {
  let listener = (e: StorageEvent) => {
    if (e.storageArea === storageArea && e.key === TOKEN_KEY) {
      if (e.oldValue !== e.newValue) {
        const token = e.newValue ? e.newValue : undefined;
        if (debug) {
          console.log('Auth.tokenUpdatedEvent', token, e);
        }

        setSopToken(token);

        callback();
      }
    }
  };

  window.addEventListener('storage', listener);
  checkSopToken();

  return () => {
    window.removeEventListener('storage', listener);
    clearTimeout(sopTokenCheckTimeout);
  };
};
